import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { ThemeToggle } from "@/components/ThemeToggle";
import { CATEGORIES } from "@/lib/leadMeta";
import {
  Shield, MapPin, Share2, ArrowRight, Heart, Activity, Car, Flower2, CheckCircle2, Building2,
  DollarSign, CalendarDays, BookOpen, FileDown, MessageSquare, Map, Sparkles,
} from "lucide-react";

const fadeUp = {
  hidden: { opacity: 0, y: 24 },
  show: (i = 0) => ({ opacity: 1, y: 0, transition: { duration: 0.5, delay: i * 0.08 } }),
};

const products = [
  { key: "life", icon: Heart, desc: "Term, whole & mortgage protection leads ready to quote." },
  { key: "health", icon: Activity, desc: "Individual, family & marketplace health shoppers." },
  { key: "casualty", icon: Car, desc: "Auto, home & bundled P&C prospects in your territory." },
  { key: "final_needs", icon: Flower2, desc: "Final expense & burial coverage for seniors." },
];

const features = [
  { icon: MapPin, title: "Area-based lead finder", desc: "Search by ZIP + radius, city or state and watch matching leads light up an interactive map." },
  { icon: Share2, title: "Cross-agency lead sharing", desc: "Assign, claim and share leads across teams and partner agencies with full role-based control." },
  { icon: DollarSign, title: "Commission tracking", desc: "Auto-calculate projected and earned commissions, with downloadable monthly statements per agent." },
  { icon: CalendarDays, title: "Appointment planner", desc: "Book appointments, set to-dos and get reminders on a built-in calendar that keeps agents on track." },
  { icon: BookOpen, title: "Automated Book of Business", desc: "Turn closed-won leads into active policies automatically and manage your entire book in one place." },
  { icon: FileDown, title: "Branded PDF & Excel exports", desc: "Export leads, commissions and reports with your own logo, brand color and agency address." },
  { icon: MessageSquare, title: "Live team messaging", desc: "Chat with teammates and approved partners in real time, right inside the platform." },
  { icon: Map, title: "Territory claiming & heatmaps", desc: "Claim territories and visualize lead density across your region with interactive heatmaps." },
  { icon: Sparkles, title: "Dynamic AI call scripts", desc: "Generate tailored call scripts per lead so agents open every conversation with confidence." },
];

export default function Landing() {
  return (
    <div className="min-h-screen bg-white text-slate-900 transition-colors dark:bg-[#0B0F19] dark:text-white">
      {/* Nav */}
      <header className="fixed inset-x-0 top-0 z-50 border-b border-slate-200/70 bg-white/70 glass dark:border-slate-800/70 dark:bg-slate-950/70">
        <nav className="mx-auto flex max-w-7xl items-center justify-between px-6 py-4">
          <div className="flex flex-col">
            <div className="flex items-center gap-2 font-heading text-lg font-bold">
              <Shield className="h-6 w-6 text-primary" /> IShareLeads
            </div>
            <span className="pl-8 font-mono text-[11px] uppercase tracking-widest text-teal-600 dark:text-teal-400" data-testid="logo-tagline">Insurance Lead Sharing</span>
          </div>
          <div className="hidden items-center gap-8 text-sm text-slate-600 md:flex dark:text-slate-300">
            <a href="#products" className="hover:text-slate-900 dark:hover:text-white">Products</a>
            <a href="#area" className="hover:text-slate-900 dark:hover:text-white">Area Finder</a>
            <a href="#features" className="hover:text-slate-900 dark:hover:text-white">Features</a>
          </div>
          <div className="flex items-center gap-2">
            <ThemeToggle />
            <Link to="/login"><Button variant="ghost" data-testid="nav-login-button" className="text-slate-700 hover:text-slate-900 dark:text-slate-200 dark:hover:text-white">Portal Login</Button></Link>
            <Link to="/register"><Button data-testid="nav-signup-button">Start Free</Button></Link>
          </div>
        </nav>
      </header>

      {/* Hero */}
      <section className="relative overflow-hidden px-6 pb-24 pt-40">
        <div className="pointer-events-none absolute inset-0 grid-bg opacity-20 dark:opacity-40" />
        <div className="pointer-events-none absolute left-1/2 top-24 h-96 w-96 -translate-x-1/2 rounded-full bg-primary/20 blur-[120px]" />
        <div className="relative mx-auto max-w-5xl text-center">
          <motion.span initial="hidden" animate="show" variants={fadeUp}
            className="inline-flex items-center gap-2 rounded-full border border-slate-300 bg-slate-100 px-4 py-1.5 font-mono text-xs uppercase tracking-widest text-teal-600 dark:border-slate-700 dark:bg-slate-900/60 dark:text-teal-400">
            <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-teal-500 dark:bg-teal-400" /> Multi-tenant leads platform
          </motion.span>
          <motion.h1 initial="hidden" animate="show" custom={1} variants={fadeUp}
            className="mx-auto mt-6 max-w-4xl font-heading text-4xl font-extrabold leading-tight tracking-tight sm:text-5xl lg:text-6xl">
            Every insurance lead. <span className="text-primary">Mapped, shared & closed.</span>
          </motion.h1>
          <motion.p initial="hidden" animate="show" custom={2} variants={fadeUp}
            className="mx-auto mt-6 max-w-2xl text-base leading-relaxed text-slate-600 sm:text-lg dark:text-slate-400">
            The command center for life, health, casualty & final needs agencies. Find leads by area, share them across your team, and turn territory into revenue.
          </motion.p>
          <motion.div initial="hidden" animate="show" custom={3} variants={fadeUp} className="mt-10 flex flex-wrap items-center justify-center gap-4">
            <Link to="/register"><Button size="lg" data-testid="hero-signup-button" className="group">Launch your agency <ArrowRight className="ml-2 h-4 w-4 transition-transform group-hover:translate-x-1" /></Button></Link>
            <a href="#area"><Button size="lg" variant="outline" data-testid="hero-demo-button" className="border-slate-300 bg-transparent text-slate-900 hover:bg-slate-100 dark:border-slate-700 dark:text-white dark:hover:bg-slate-800">See the area finder</Button></a>
          </motion.div>
          <motion.div initial="hidden" animate="show" custom={4} variants={fadeUp} className="mt-14 grid grid-cols-3 gap-4 border-t border-slate-200 pt-8 sm:mx-auto sm:max-w-2xl dark:border-slate-800">
            {[["24+", "Live sample leads"], ["4", "Coverage lines"], ["3", "Team roles"]].map(([n, l]) => (
              <div key={l}>
                <p className="font-mono text-3xl font-bold text-slate-900 dark:text-white">{n}</p>
                <p className="mt-1 text-xs text-slate-500">{l}</p>
              </div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Products */}
      <section id="products" className="mx-auto max-w-7xl px-6 py-20">
        <h2 className="font-heading text-2xl font-bold sm:text-3xl lg:text-4xl">Built for every line you write</h2>
        <p className="mt-3 max-w-xl text-slate-600 dark:text-slate-400">Purpose-built lead pipelines for the four coverage types your agency sells.</p>
        <div className="mt-10 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4">
          {products.map((p, i) => {
            const cat = CATEGORIES[p.key];
            return (
              <motion.div key={p.key} initial="hidden" whileInView="show" viewport={{ once: true }} custom={i} variants={fadeUp}
                className="group rounded-2xl border border-slate-200 bg-slate-50 p-6 transition-colors hover:border-primary/40 dark:border-slate-800 dark:bg-slate-900/50">
                <div className="flex h-12 w-12 items-center justify-center rounded-xl" style={{ backgroundColor: cat.color + "22" }}>
                  <p.icon className="h-6 w-6" style={{ color: cat.color }} />
                </div>
                <h3 className="mt-4 font-heading text-lg font-semibold">{cat.label}</h3>
                <p className="mt-2 text-sm text-slate-600 dark:text-slate-400">{p.desc}</p>
              </motion.div>
            );
          })}
        </div>
      </section>

      {/* Area finder highlight */}
      <section id="area" className="border-y border-slate-200 bg-slate-50 px-6 py-20 dark:border-slate-800 dark:bg-slate-950/40">
        <div className="mx-auto grid max-w-7xl items-center gap-12 lg:grid-cols-2">
          <div>
            <span className="font-mono text-xs uppercase tracking-widest text-teal-600 dark:text-teal-400">Find leads by area</span>
            <h2 className="mt-3 font-heading text-2xl font-bold sm:text-3xl lg:text-4xl">Type a ZIP. Set a radius. Work your territory.</h2>
            <p className="mt-4 text-slate-600 dark:text-slate-400">IShareLeads plots every lead on an interactive map. Drop in a ZIP code, drag the radius from 5 to 50 miles, and instantly see who's in reach — filtered by coverage type and pipeline status.</p>
            <ul className="mt-6 space-y-3">
              {["ZIP + radius distance search", "City & state filtering", "Interactive map with color-coded pins", "Claim & assign leads on the spot"].map((t) => (
                <li key={t} className="flex items-center gap-3 text-sm text-slate-700 dark:text-slate-300"><CheckCircle2 className="h-5 w-5 text-primary" /> {t}</li>
              ))}
            </ul>
          </div>
          <div className="relative rounded-2xl border border-slate-800 bg-[#0a0e17] p-4 grid-bg shadow-xl">
            <div className="flex items-center gap-2 border-b border-slate-800 pb-3">
              <MapPin className="h-4 w-4 text-teal-400" />
              <span className="font-mono text-xs text-slate-400">ZIP 90012 · 25mi radius · 6 leads</span>
            </div>
            <div className="relative mt-4 h-64">
              {[[30, 40, "#2563EB"], [55, 30, "#10B981"], [45, 60, "#8B5CF6"], [70, 55, "#F59E0B"], [25, 65, "#10B981"], [60, 70, "#2563EB"]].map(([x, y, c], i) => (
                <MapPin key={i} className="pin-drop absolute h-6 w-6" style={{ left: `${x}%`, top: `${y}%`, color: c, fill: c, fillOpacity: 0.8, animationDelay: `${i * 0.1}s` }} />
              ))}
              <div className="absolute left-1/2 top-1/2 h-40 w-40 -translate-x-1/2 -translate-y-1/2 rounded-full border-2 border-teal-400/40 bg-teal-400/5" />
            </div>
          </div>
        </div>
      </section>

      {/* Features */}
      <section id="features" className="mx-auto max-w-7xl px-6 py-20">
        <h2 className="font-heading text-2xl font-bold sm:text-3xl lg:text-4xl">Everything an agency needs</h2>
        <p className="mt-3 max-w-xl text-slate-600 dark:text-slate-400">From first touch to closed policy — find, share, sell and get paid, all in one workspace.</p>
        <div className="mt-10 grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
          {features.map((f, i) => (
            <motion.div key={f.title} initial="hidden" whileInView="show" viewport={{ once: true }} custom={i} variants={fadeUp}
              className="rounded-2xl border border-slate-200 bg-slate-50 p-6 transition-colors hover:border-primary/40 dark:border-slate-800 dark:bg-slate-900/50">
              <f.icon className="h-6 w-6 text-primary" />
              <h3 className="mt-4 font-heading text-lg font-semibold">{f.title}</h3>
              <p className="mt-2 text-sm text-slate-600 dark:text-slate-400">{f.desc}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="px-6 pb-24">
        <div className="relative mx-auto max-w-5xl overflow-hidden rounded-3xl border border-slate-800 bg-gradient-to-br from-slate-900 to-slate-950 p-12 text-center text-white shadow-xl">
          <div className="pointer-events-none absolute left-1/2 top-0 h-64 w-64 -translate-x-1/2 rounded-full bg-primary/20 blur-[100px]" />
          <Building2 className="mx-auto h-10 w-10 text-primary" />
          <h2 className="relative mt-4 font-heading text-3xl font-extrabold sm:text-4xl">Turn your territory into a pipeline.</h2>
          <p className="relative mx-auto mt-4 max-w-xl text-slate-400">Spin up your agency workspace free. No credit card required.</p>
          <Link to="/register" className="relative"><Button size="lg" data-testid="cta-signup-button" className="mt-8">Create your free agency <ArrowRight className="ml-2 h-4 w-4" /></Button></Link>
        </div>
      </section>

      <footer className="border-t border-slate-200 px-6 py-10 dark:border-slate-800">
        <div className="mx-auto flex max-w-7xl flex-col items-center justify-between gap-4 sm:flex-row">
          <div className="flex items-center gap-2 font-heading font-bold"><Shield className="h-5 w-5 text-primary" /> IShareLeads</div>
          <p className="font-mono text-xs text-slate-500 dark:text-slate-600">© 2026 IShareLeads Platform</p>
        </div>
      </footer>
    </div>
  );
}
